'use client';

import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface NewsletterBlockProps {
  compact?: boolean;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function NewsletterBlock({ compact = false }: NewsletterBlockProps) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();

    // Client-side validation
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const supabase = createClient();

      // Insert into newsletter_subscribers
      const { error: dbError } = await supabase
        .from('newsletter_subscribers')
        .insert({ email: trimmed, source: 'industry_briefing' });

      if (dbError) {
        if (dbError.code === '23505') {
          // Duplicate email — already subscribed, treat gracefully
          setAlreadySubscribed(true);
          setSubmitted(true);
          setLoading(false);
          return;
        }
        throw new Error(dbError.message);
      }

      // Send confirmation email via edge function (non-blocking)
      try {
        await supabase.functions.invoke('send-email', {
          body: { type: 'newsletter_confirm', to: trimmed },
        });
      } catch {
        // Email sending failure is non-blocking — subscription still recorded
      }

      setSubmitted(true);
    } catch {
      setError('Could not subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`bg-foreground text-white rounded-lg ${compact ? 'p-5' : 'p-6 lg:p-8'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Mail size={16} className="text-primary" />
        <span className="text-xs font-700 uppercase tracking-widest text-primary">
          Industry Briefing
        </span>
      </div>
      <h3 className={`font-700 text-white leading-tight ${compact ? 'text-base' : 'text-lg'}`}>
        The weekly intelligence digest for U.S. peptide industry professionals.
      </h3>
      {!compact && (
        <p className="mt-2 text-sm text-white/60 leading-relaxed">
          Regulatory alerts, supply chain updates, testing standards, and market data — curated for suppliers, researchers, and compliance officers.
        </p>
      )}

      {submitted ? (
        <div className="mt-4 flex items-start gap-2 text-sm text-green-400">
          <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            {alreadySubscribed
              ? "You're already subscribed — check your inbox for the latest issue."
              : "You're subscribed — check your inbox to confirm."}
          </span>
        </div>
      ) : (
        <>
          {error && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
              <AlertCircle size={13} />
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-2" noValidate>
            <input
              type="email"
              placeholder="your@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              className="flex-1 px-3 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-shrink-0 justify-center min-w-[120px]"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Subscribing
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  Subscribe Free <ArrowRight size={14} />
                </span>
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );
}